import { eventBus, GameEvents } from '../systems/events.js';
import { getGameState, saveGameState } from '../systems/state.js';
import { DISCOVERY } from '../systems/balance.js';

// Discovery names mapped by interactable id prefix
const DISCOVERY_MAP = {
  // Base creatures (MVP)
  crab: { name: 'Red Crab', icon: '\uD83E\uDD80', category: 'creatures' },
  dolphin: { name: 'Dolphin', icon: '\uD83D\uDC2C', category: 'creatures' },
  seagull: { name: 'Seagull', icon: '\uD83D\uDC26', category: 'creatures' },
  // Base objects (MVP)
  treasure_chest: { name: 'Treasure Chest', icon: '\uD83C\uDF81', category: 'objects' },
  palmtree: { name: 'Palm Tree', icon: '\uD83C\uDF34', category: 'objects' },
  welcome_sign: { name: 'Welcome Sign', icon: '\uD83E\uDEA7', category: 'objects' },
  beer_bottle: { name: 'Beer Bottle', icon: '\uD83C\uDF7A', category: 'objects' },
  beach_bum: { name: 'Beach Bum', icon: '\uD83C\uDFB8', category: 'objects' },
  // Creature bonuses (Phase 1 Full)
  crab_wave: { name: 'Crab Wave', icon: '\uD83D\uDC4B', category: 'bonuses' },
  crab_stampede: { name: 'Crab Stampede', icon: '\uD83D\uDCA8', category: 'bonuses' },
  barrel_roll: { name: 'Barrel Roll', icon: '\uD83C\uDF00', category: 'bonuses' },
  show_off: { name: 'Show Off', icon: '\u2B50', category: 'bonuses' },
  synchronized_splash: { name: 'Synchronized Splash', icon: '\uD83C\uDFCA', category: 'bonuses' },
  formation_flight: { name: 'Formation Flight', icon: '\u2708\uFE0F', category: 'bonuses' },
  golden_gull: { name: 'Golden Gull', icon: '\uD83E\uDD86', category: 'bonuses' },
  // Object bonuses (Phase 1 Full)
  music_note_catch: { name: 'Music Note', icon: '\uD83C\uDFB5', category: 'bonuses' },
  melody_maker: { name: 'Melody Maker', icon: '\uD83C\uDFB6', category: 'bonuses' },
  cheers: { name: 'Cheers!', icon: '\uD83C\uDF7B', category: 'bonuses' },
  recycler: { name: 'Recycler', icon: '\u267B\uFE0F', category: 'bonuses' },
  guitar_solo: { name: 'Guitar Solo', icon: '\uD83C\uDFB8', category: 'bonuses' },
  jam_session: { name: 'Jam Session', icon: '\uD83C\uDFB6', category: 'bonuses' },
  // Hidden secrets (pi-3rt)
  buried_seashell: { name: 'Buried Seashell', icon: '\uD83D\uDC1A', category: 'secrets' },
  tide_pool: { name: 'Tide Pool', icon: '\uD83C\uDF0A', category: 'secrets' },
  message_in_bottle: { name: 'Message in a Bottle', icon: '\uD83C\uDF76', category: 'secrets' },
  hermit_crab: { name: 'Hermit Crab', icon: '\uD83D\uDC1A', category: 'secrets' },
  // Milestone creatures (pi-wfm)
  sea_turtle: { name: 'Sea Turtle', icon: '\uD83D\uDC22', category: 'creatures' },
  // Combination secrets (pi-d7w)
  crab_orchestra: { name: 'Crab Orchestra', icon: '\uD83C\uDFB6', category: 'combos' },
  dolphin_serenade: { name: 'Dolphin Serenade', icon: '\uD83C\uDFB5', category: 'combos' },
};

// Map an interactable id to its discovery key
function getDiscoveryKey(id) {
  // Match exact keys first (e.g. "treasure_chest", "beach_bum", "welcome_sign")
  if (DISCOVERY_MAP[id]) return id;
  // Then match by prefix (e.g. "crab_0" -> "crab", "dolphin_2" -> "dolphin")
  for (const key of Object.keys(DISCOVERY_MAP)) {
    if (id.startsWith(key)) return key;
  }
  return null;
}

// Toast notification queue
const toastQueue = [];
let toastActive = false;
let toastContainer = null;

function createToastContainer() {
  toastContainer = document.createElement('div');
  toastContainer.id = 'discovery-toast-container';
  toastContainer.style.cssText = `
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 1000;
    pointer-events: none;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
  `;
  document.body.appendChild(toastContainer);
}

function showToast(icon, name, duration = DISCOVERY.TOAST_DURATION_STANDARD) {
  toastQueue.push({ icon, name, duration });
  processToastQueue();
}

function processToastQueue() {
  if (toastActive || toastQueue.length === 0) return;
  toastActive = true;

  const { icon, name, duration } = toastQueue.shift();

  const state = getGameState();
  const totalDiscovered = Object.keys(state.discoveries).length;
  const totalPossible = Object.keys(DISCOVERY_MAP).length;

  const toast = document.createElement('div');
  toast.style.cssText = `
    background: linear-gradient(135deg, #f5e6c8 0%, #e8d5a3 100%);
    border: 2px solid #c4a265;
    border-radius: 12px;
    padding: 12px 20px;
    font-family: 'Georgia', serif;
    font-size: 16px;
    color: #5a3e1b;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
    opacity: 0;
    transform: translateX(100px);
    transition: opacity 0.4s ease, transform 0.4s ease;
    display: flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
  `;

  const progressText = totalDiscovered >= DISCOVERY.TOAST_PROGRESS_THRESHOLD
    ? ` <span style="font-size: 13px; opacity: 0.7;">(${totalDiscovered}/${totalPossible})</span>`
    : '';

  toast.innerHTML = `
    <span style="font-size: 22px;">${icon}</span>
    <span>${name} discovered!${progressText}</span>
  `;

  toastContainer.appendChild(toast);

  // Trigger slide-in
  requestAnimationFrame(() => {
    toast.style.opacity = '1';
    toast.style.transform = 'translateX(0)';
  });

  // Auto-dismiss after duration
  setTimeout(() => {
    toast.style.opacity = '0';
    toast.style.transform = 'translateX(100px)';
    setTimeout(() => {
      toast.remove();
      toastActive = false;
      // Process next in queue with a small gap
      setTimeout(processToastQueue, DISCOVERY.TOAST_GAP);
    }, 400);
  }, duration);
}

function handleInteraction(data) {
  if (!data || !data.id) return;

  const discoveryKey = getDiscoveryKey(data.id);
  if (!discoveryKey) return;

  const info = DISCOVERY_MAP[discoveryKey];
  const state = getGameState();

  // Update total clicks
  state.stats.totalClicks++;

  if (state.discoveries[discoveryKey]) {
    // Already discovered, just increment count
    state.discoveries[discoveryKey].count++;
  } else {
    // New discovery
    state.discoveries[discoveryKey] = {
      discovered: true,
      timestamp: Date.now(),
      count: 1,
    };

    // Show toast — tier 1 (secrets/combos) gets longer duration
    const isHighTier = info.category === 'secrets' || info.category === 'combos';
    showToast(info.icon, info.name, isHighTier ? DISCOVERY.TOAST_DURATION_HIGH_TIER : DISCOVERY.TOAST_DURATION_STANDARD);

    // Emit new discovery event
    eventBus.emit(GameEvents.DISCOVERY_NEW, {
      key: discoveryKey,
      name: info.name,
      icon: info.icon,
      category: info.category,
      totalDiscovered: Object.keys(state.discoveries).length,
      totalPossible: Object.keys(DISCOVERY_MAP).length,
    });
  }

  saveGameState(state);
}

export function getDiscoveryMap() {
  return DISCOVERY_MAP;
}

export function getDiscoveryCount() {
  const state = getGameState();
  return {
    discovered: Object.keys(state.discoveries).length,
    total: Object.keys(DISCOVERY_MAP).length,
  };
}

export function initDiscovery() {
  createToastContainer();

  // Listen for creature and object click events
  eventBus.on(GameEvents.CREATURE_CLICKED, handleInteraction);
  eventBus.on(GameEvents.OBJECT_CLICKED, handleInteraction);

  console.log('[Discovery] System initialized');
}
